import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators, FormControl, AbstractControl, ValidationErrors } from "@angular/forms";
import { EstudanteService } from "app/service/estudante/estudante.service";
import { Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { MAT_DATE_LOCALE, NativeDateAdapter, DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';


export class CustomDateAdapter extends NativeDateAdapter {
    override parse(value: any): Date | null {
        if (typeof value === 'string' && value.trim()) {
            const parts = value.split('/');
            if (parts.length === 3) {
                const day = parseInt(parts[0], 10);
                const month = parseInt(parts[1], 10) - 1;
                const year = parseInt(parts[2], 10);
                if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
                    return new Date(year, month, day);
                }
            }
        }
        return null;
    }

    override format(date: Date, displayFormat: Object): string {
        if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
            return '';
        }
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    }
}

export const MY_DATE_FORMATS = {
    parse: { dateInput: 'DD/MM/YYYY' },
    display: {
        dateInput: 'DD/MM/YYYY',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'DD/MM/YYYY',
        monthYearA11yLabel: 'MMMM YYYY',
    },
};

@Component({
    selector: "app-estudante",
    templateUrl: "./estudante.component.html",
    styleUrls: ["./estudante.component.scss"],
    providers: [
        { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
        { provide: DateAdapter, useClass: CustomDateAdapter },
        { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    ],
})
export class EstudanteComponent implements OnInit {
    formCadastro: FormGroup;

    constructor(
        private fb: FormBuilder,
        private estudanteService: EstudanteService,
        private router: Router,
        private toasterService: ToastrService,
    ) { }

    ngOnInit(): void {
        this.formCadastro = this.fb.group({
            nomeCompleto: new FormControl("", Validators.required),
            nomeSocial: new FormControl(""),
            sexo: new FormControl("", Validators.required),
            genero: new FormControl("", Validators.required),
            dataNascimento: new FormControl("", [Validators.required, this.invalidDataNasc]),
            cpf: new FormControl("", [Validators.required, this.cpfValidator]),
            estadoCivil: new FormControl("", Validators.required),

            // Grupo Contato ajustado: Sem telefone pessoal para não travar a validação
            contato: this.fb.group({
                email: new FormControl("", [Validators.required, this.emailValidator]),
                emailAlternativo: new FormControl("", [this.emailValidator]),
                telefonePessoal: new FormControl("", [Validators.required]), // Celular
                telefoneFixo: new FormControl(""), // Fixo opcional
            }),

            formacao: this.fb.group({
                instituicaoEnsino: new FormControl("", Validators.required),
                curso: new FormControl("", Validators.required),
                nivelEscolaridade: new FormControl("", Validators.required),
                periodoAtual: new FormControl("", Validators.required),
            }),
        });
    }

    private emailValidator(control: AbstractControl): ValidationErrors | null {
        const email = control.value;
        if (!email || email.trim() === '') { return null; }
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return emailRegex.test(email) ? null : { invalidEmail: true };
    }

    private invalidDataNasc(control: AbstractControl): ValidationErrors | null {
        const data = control.value;
        return (data instanceof Date && !isNaN(data.getTime())) ? null : { dataInvalida: true };
    }

    private cpfValidator(control: AbstractControl): ValidationErrors | null {
        const cpf = control.value;
        if (cpf) {
            if (cpf.length < 11) return null;
            let equalDigits = 1;
            for (let i = 0; i < cpf.length - 1; i++) {
                if (cpf.charAt(i) !== cpf.charAt(i + 1)) { equalDigits = 0; break; }
            }
            if (equalDigits) return { invalidCPF: true };

            let numbers = cpf.substring(0, 9);
            let digits = cpf.substring(9);
            let sum = 0;
            for (let i = 10; i > 1; i--) sum += numbers.charAt(10 - i) * i;
            let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
            if (result !== Number(digits.charAt(0))) return { invalidCPF: true };

            numbers = cpf.substring(0, 10);
            sum = 0;
            for (let i = 11; i > 1; i--) sum += numbers.charAt(11 - i) * i;
            result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
            return result !== Number(digits.charAt(1)) ? { invalidCPF: true } : null;
        }
        return null;
    }

    onSubmit(): void {
        console.log("Iniciando processo de salvamento...");

        if (this.formCadastro.invalid) {
            console.log("Campos com erro:", this.findInvalidControls());
            this.formCadastro.markAllAsTouched();
            this.toasterService.warning("Por favor, preencha todos os campos obrigatórios corretamente.");
            return;
        }

        const estudante = { ...this.formCadastro.value };
        if (estudante.dataNascimento instanceof Date) {
            estudante.dataNascimento = estudante.dataNascimento.toISOString().split('T')[0];
        }

        console.log("Objeto enviado:", estudante);

        this.estudanteService.salvarEstudante(estudante).subscribe({
            next: retorno => {
                this.formCadastro.reset();
                this.router.navigate(["/confirmado-cadastro-estudante"], {
                    state: { emailEstudante: retorno.contato.email },
                });
            },
            error: error => {
                const campoSimplesDTO = error.error[0].error.split(".").pop();
                if (campoSimplesDTO == "cpf-ja-registrado") {
                    this.toasterService.warning(error.error[0].message);
                } else if (campoSimplesDTO == "email-ja-registrado" || campoSimplesDTO == "email-alternativo-ja-registrado") {
                    this.toasterService.warning(error.error[0].message);
                }
            },
        });
    }

    // Função auxiliar para te ajudar a debugar se ainda der erro
    private findInvalidControls() {
        const invalid = [];
        const controls = this.formCadastro.controls;
        for (const name in controls) {
            if (controls[name].invalid) { invalid.push(name); }
        }
        const contatoControls = (this.formCadastro.get('contato') as FormGroup).controls;
        for (const name in contatoControls) {
            if (contatoControls[name].invalid) { invalid.push('contato.' + name); }
        }
        return invalid;
    }

    onVoltar() { this.router.navigate(["/login"]); }
}